import { ChangeEvent, FC } from 'react'
import { FastField, FastFieldProps } from 'formik'
import { TextField, InputAdornment, Box } from '@mui/material'
import { Check } from '@dhl/icons'
import { Autocomplete } from '@dhl/components'
import { FieldName, Autocomplete as AutocompleteFieldFormat } from '@dhl/receiver-address-format-api'
import { identity } from '@dhl/utils'

import { AddressLabel } from './AddressLabel.js'

type Props = {
  name: string
  fieldName: FieldName
  fieldFormat: AutocompleteFieldFormat
  options: string[]
  isLoading: boolean
  disabled?: boolean
}

export const AddressAutocompleteFieldInput: FC<Props> = ({
  name,
  fieldName,
  fieldFormat,
  options,
  isLoading,
  disabled,
}) => {
  const isReadonly = (fieldFormat.isStrict && options.length === 1) || disabled

  return (
    <FastField name={name}>
      {({ field, meta }: FastFieldProps) => (
        <>
          {isReadonly && (
            <TextField
              {...field}
              name={field.name}
              value={field.value || ''}
              variant="outlined"
              required={fieldFormat.isRequired}
              label={<AddressLabel fieldName={fieldName} />}
              fullWidth
              disabled
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box display="flex" mr={1}>
                        <Check htmlColor="green" />
                      </Box>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          {!isReadonly && (
            <Autocomplete<string, boolean>
              id={fieldName}
              name={field.name}
              value={field.value}
              onChange={(_: ChangeEvent<{}>, value: string | null) => value && field.onChange(field.name)(value)}
              label={<AddressLabel fieldName={fieldName} />}
              error={!!meta.error && meta.touched}
              helperText={meta.touched && meta.error}
              required={fieldFormat.isRequired}
              freeSolo={!fieldFormat.isStrict}
              options={options}
              getOptionLabel={identity}
              isLoading={isLoading}
              fullWidth
              disabled={disabled}
            />
          )}
        </>
      )}
    </FastField>
  )
}
